// One shopping-list row.
//
// Used by the main category sections AND the "From your imported recipes"
// matched section.
//
// Props:
//   item               — { id, name, unit, packs, size, needed, leftover, cost,
//                          (Tranche B) alternatives?, pinned?
//                          (Tranche C) pantried? }
//   subtitle           — optional override for the second line
//   usedIn             — optional string: meal/recipe names this item is used in,
//                        rendered as its own line under the name (does not replace
//                        the needed/buy subtitle)
//   priceyThreshold    — number (default 5)
//   rightSlot          — optional render override for the right-hand price area
//   alternatives       — [{pkgId, pkgName, score, costAtRetailer}] swap chips
//   onSwap(pkgId)      — called when user taps a swap chip
//   pantried           — boolean: item is in user's pantry (dimmed + strikethrough)
//   onOwn()            — called when Own/Owned button tapped
//   onRoundDownChoice(scenario) — called with "roundDown" | "buyExtra" when the
//                        user resolves a small-shortfall pack choice

import { C } from "../theme.js";

// Is this item only slightly short once we drop the extra pack? The server
// flags eligibility (shortfall ≤ 20% of needed); the client just renders the choice.
export function roundDownChoiceState(item) {
  const opt = item?.roundDownOption;
  if (!opt || !opt.eligible) return null;
  // Which scenario is currently applied? Compare the live pack count to the
  // round-down scenario's pack count.
  const applied = Number(item.packs) === Number(opt.roundDown.packs) ? "roundDown" : "buyExtra";
  return { opt, applied };
}

export function shortfallState(item) {
  const requiredQty = Number(item?.needed) || 0;
  const purchaseQty = (Number(item?.packs) || 0) * (Number(item?.size) || 0);
  const shortfall = Math.max(requiredQty - purchaseQty, 0);
  const shortfallPercent = requiredQty > 0 ? shortfall / requiredQty : 0;

  if (shortfall <= 0) return { kind: "none", shortfall: 0, shortfallPercent: 0 };
  if (shortfallPercent <= 0.10) return { kind: "small", shortfall, shortfallPercent };
  if (shortfallPercent <= 0.25) return { kind: "moderate", shortfall, shortfallPercent };
  return { kind: "major", shortfall, shortfallPercent };
}

export function leftoverState(item) {
  const requiredQty = Number(item?.needed) || 0;
  const purchaseQty = (Number(item?.packs) || 0) * (Number(item?.size) || 0);
  const leftover = Math.max(purchaseQty - requiredQty, 0);
  const leftoverPercent = purchaseQty > 0 ? leftover / purchaseQty : 0;

  if (leftover <= 0) return { kind: "none", leftover: 0, leftoverPercent: 0 };
  if (leftoverPercent <= 0.25) return { kind: "small", leftover, leftoverPercent };
  if (leftoverPercent <= 0.60) return { kind: "useful", leftover, leftoverPercent };
  return { kind: "large", leftover, leftoverPercent };
}

export function packShortfallMessage(item) {
  if (!item) return null;
  const state = shortfallState(item);
  if (state.kind === "none") return null;
  if (state.kind === "small") return "Rounded down to reduce waste.";
  if (state.kind === "moderate") return "Add another pack for exact quantity.";
  return "Add another pack to avoid running out.";
}

export function ListItemRow({
  item,
  subtitle,
  usedIn,
  priceyThreshold = 5,
  rightSlot,
  borderBottom = true,
  alternatives,
  onSwap,
  pantried = false,
  onOwn,
  onRoundDownChoice,
}) {
  const isPricey   = item.cost > priceyThreshold;
  const unitLabel  = item.unit === "count" ? "" : item.unit;
  const alts       = alternatives ?? item.alternatives ?? [];
  const firstOriginalAltIndex = item.original ? -1 : alts.findIndex(alt => alt.original === true);
  const hasPinBadge = item.pinned === true;
  const dimmed     = pantried;
  const shortfallMessage = packShortfallMessage(item);
  const shortfall = Math.round(shortfallState(item).shortfall);
  const shortfallKind = shortfallState(item).kind;
  const shortfallColor = shortfallKind === "major" || shortfallKind === "moderate" ? C.amber : C.gold;
  const roundDown  = !pantried ? roundDownChoiceState(item) : null;

  return (
    <div style={{
      display:"flex", flexDirection:"column",
      padding:"12px 16px",
      borderBottom: borderBottom ? `1px solid ${C.border}` : "none",
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        {/* Left: name + subtitle (dimmed when pantried) */}
        <div style={{ minWidth:0, flex:1, opacity: dimmed ? 0.45 : 1 }}>
          <div style={{
            fontWeight:700, fontSize:14, color:C.charcoal,
            textDecoration: dimmed ? "line-through" : "none",
            display:"flex", alignItems:"center", gap:5,
          }}>
            {item.name}
            {hasPinBadge && (
              <span style={{
                fontSize:10, background:C.green, color:"#fff",
                borderRadius:6, padding:"1px 5px", fontWeight:600,
              }}>
                Pinned
              </span>
            )}
          </div>
          {usedIn && (
            <div style={{ fontSize:11.5, color:C.teal, fontWeight:600, marginTop:2 }}>
              Used in: {usedIn}
            </div>
          )}
          <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>
            {subtitle ?? (
              item.pantryStaple ? (
                <>
                  1 × {item.size}{unitLabel} pack ·{" "}
                  <span style={{ color:C.teal, fontStyle:"italic" }}>buy if needed</span>
                </>
              ) : (
                <>
                  {item.packs} × {item.size}{unitLabel} pack ·{" "}
                  {shortfallKind !== "none" ? (
                    <span
                      title={shortfallMessage}
                      aria-label={`${item.needed}${unitLabel} needed, ${shortfall}${unitLabel} short. ${shortfallMessage}`}
                      style={{ cursor:"help", borderBottom:`1px dotted ${shortfallColor}`, color:shortfallColor }}
                    >
                      ~{item.needed}{unitLabel} needed · {shortfall}{unitLabel} short
                    </span>
                  ) : (
                    <>{item.needed}{unitLabel} needed</>
                  )}
                  {item.leftover > 0 && item.unit !== "count" && (
                    <span style={{ color:C.amber }}> · {item.leftover}{item.unit} leftover</span>
                  )}
                </>
              )
            )}
          </div>
        </div>

        {/* Right: price + Own button */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", paddingLeft:12, gap:4 }}>
          <div style={{
            fontWeight:800, fontSize:15,
            color: dimmed ? C.grey : (isPricey ? C.amber : C.charcoal),
            whiteSpace:"nowrap",
            opacity: dimmed ? 0.45 : 1,
          }}>
            {rightSlot ?? (
              <>
                {isPricey && !dimmed && <span style={{ fontSize:12, marginRight:4 }}>⚠</span>}
                £{item.cost.toFixed(2)}
              </>
            )}
          </div>
          {onOwn && (
            <button
              onClick={(e) => { e.stopPropagation(); onOwn(); }}
              style={{
                fontSize:10, fontWeight:700, padding:"2px 7px",
                border:`1px solid ${pantried ? C.green : C.border}`,
                borderRadius:6,
                background: pantried ? C.greenPale : C.white,
                color:      pantried ? C.green : C.grey,
                cursor:"pointer", whiteSpace:"nowrap",
              }}
            >
              {pantried ? "Owned ✓" : "Own"}
            </button>
          )}
        </div>
      </div>

      {/* Small-shortfall pack choice — only a little short once the extra pack is
          dropped, so let the user trade a tiny shortfall for less waste + spend.
          Framed as a smart saving, not an error. */}
      {roundDown && onRoundDownChoice && (
        <div style={{
          marginTop:8, padding:"8px 10px",
          background:C.greenPale, border:`1px solid ${C.green}`, borderRadius:10,
        }}>
          <div style={{ fontSize:11.5, color:C.green, fontWeight:600, marginBottom:6 }}>
            ♻ Only {Math.round(roundDown.opt.shortfall)}{unitLabel} short — skip the extra pack and save £{roundDown.opt.savings.toFixed(2)}?
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {[
              { key:"roundDown", label:`Round down · save £${roundDown.opt.savings.toFixed(2)}` },
              { key:"buyExtra",  label:"Buy extra pack" },
            ].map(({ key, label }) => {
              const selected = roundDown.applied === key;
              return (
                <button
                  key={key}
                  onClick={(e) => { e.stopPropagation(); if (!selected) onRoundDownChoice(key); }}
                  aria-pressed={selected}
                  style={{
                    fontSize:11, fontWeight:700, padding:"4px 10px",
                    border:`1px solid ${selected ? C.green : C.border}`,
                    borderRadius:8,
                    background: selected ? C.green : C.white,
                    color:      selected ? "#fff" : C.charcoal,
                    cursor: selected ? "default" : "pointer", whiteSpace:"nowrap",
                  }}
                >
                  {selected ? "✓ " : ""}{label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Alternative swap chips — hidden when pantried (no point swapping owned items) */}
      {!pantried && alts.length > 0 && onSwap && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
          {alts.map((alt, index) => (
            <button
              key={alt.pkgId}
              onClick={() => onSwap(alt.pkgId)}
              style={{
                fontSize:11, fontWeight:600, padding:"3px 8px",
                border:`1px solid ${C.border}`, borderRadius:8,
                background:C.sand, color:C.charcoal,
                cursor:"pointer", whiteSpace:"nowrap",
              }}
            >
              {index === firstOriginalAltIndex ? "Original" : "Swap"} → {alt.pkgName} £{alt.costAtRetailer.toFixed(2)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
